import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import {
  divider,
  text,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { selectPrimaryCreditCard } from 'ducks/billing/selectors'
import { selectBillingAccount } from 'ducks/billing/selectors/selectBillingAccount'
import { selectUpgradeBreakdown } from 'ducks/billing/selectors/selectUpgradeBreakdown'
import { doFetchBillingData } from 'ducks/billing/operations'
import { strftime } from 'util/date'
import { isEmpty } from 'util/objects'
import { getFormattedPrice } from 'util/numbers'
import debug from 'util/debug'
import { useBilling, usePlanChangeInfo } from 'ducks/billing/hooks'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_BILLING_EXTERNAL_APPROVE } from 'ducks/drawers/types'
import { selectIsBootstrapTypedInStates } from 'selectors/bootstrap/makeSelectIsBootstrapTypesLoaded'
import { capitalize } from 'util/strings'
import { styles as menuWithSearchStyles } from 'subapps/settings/components/MenuWithSearch/'
import { INBOX, KB } from 'ducks/billing/productTypes'
import PlanRow from '../PlanRow'
import { useBillingModals } from '../../../dataTables/BillingPlanDataTable/hooks'
import { CardFailureModal } from '../../../BillingModals'
import { AdminAccessDrawer } from '../../NoAccess'
import { styles } from '../styles'

const PRODUCT_TO_PRODUCT_NAME = {
  [KB]: 'Knowledge base',
  [INBOX]: app.t('Mailbox'),
}

const BillingUpgradePlan = ({
  open,
  onExit,
  drawerResourceId: planId,
  drawerProductType,
  drawerBillingCycle,
}) => {
  const dispatch = useDispatch()
  const primaryCard = useSelector(selectPrimaryCreditCard) || {}
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    currentInbox,
    currentKb,
    showCardError,
    updateSubscription,
    calculateEstimatedTotalBill,
  } = useBilling()

  const planChangeInfo = usePlanChangeInfo(
    planId,
    drawerBillingCycle,
    drawerProductType
  )

  const {
    current: {
      displayName: currentPlanDisplayName,
      plan: currentPlan,
      lineItem: { itemPrice = 0, cyclePrice = 0 },
      billingCycle: currentBillingCycle,
    },
    new: {
      displayName: newPlanDisplayName,
      plan: newPlan,
      lineItem: { itemPrice: newItemPrice = 0, cyclePrice: newCyclePrice = 0 },
      billingCycle: newBillingCycle,
    },
    billingEstimate: { nextBillDate, currency = 'USD' },
    isCycleChange,
    isUpgradingToAnnual,
    currentUsageUsers,
    includeOtherProductInBreakdown,
    otherProduct: {
      productType: otherProductType,
      billingCycle: otherProductBillingCycle,
      planId: otherProductPlanId,
    },
  } = planChangeInfo

  const otherProductPlanChangeInfo = usePlanChangeInfo(
    otherProductPlanId,
    otherProductBillingCycle,
    otherProductType
  )

  const {
    current: {
      displayName: currentOtherProductPlanDisplayName,
      plan: currentOtherProductPlan,
      lineItem: {
        itemPrice: currentOtherProductItemPrice = 0,
        cyclePrice: currentOtherProductCyclePrice = 0,
      },
      billingCycle: currentOtherProductBillingCycle,
    },
    new: {
      displayName: newOtherProductPlanDisplayName,
      plan: newOtherProductPlan,
      lineItem: {
        itemPrice: newOtherProductItemPrice = 0,
        cyclePrice: newOtherProductCyclePrice = 0,
      },
      billingCycle: newOtherProductBillingCycle,
    },
    isUpgradingToAnnual: otherProductIsUpgradingToAnnual,
  } = otherProductPlanChangeInfo

  const billingAccount = useSelector(selectBillingAccount)

  const [upgradeAt] = useState(new Date().getTime())

  const {
    prorataRefund: remainingPaid,
    refundDays,
    prorataCharge: prorataCharges,
    chargeDays,
  } = useSelector(state =>
    selectUpgradeBreakdown(
      state,
      `${drawerProductType}-${currentPlan.planId}${
        currentBillingCycle === 'annual' ? '-annual' : ''
      }`,
      `${drawerProductType}-${newPlan.planId}${
        drawerBillingCycle === 'annual' ? '-annual' : ''
      }`,
      upgradeAt
    )
  )

  const isLoading =
    !useSelector(state =>
      selectIsBootstrapTypedInStates(state, ['billing'], ['LOADED'])
    ) || isEmpty(billingAccount)

  const { openDrawer: openBillingExternalApprove } = useDrawer({
    type: DRAWER_TYPE_BILLING_EXTERNAL_APPROVE,
  })

  const totalMonthlyBillForAYear = useMemo(
    () => calculateEstimatedTotalBill('monthly') * 12,
    [calculateEstimatedTotalBill]
  )

  const totalAnnualBill = useMemo(() => calculateEstimatedTotalBill('annual'), [
    calculateEstimatedTotalBill,
  ])

  const totalSavings = Math.max(0, totalMonthlyBillForAYear - totalAnnualBill)

  const onSubmit = useCallback(
    async () => {
      setIsSubmitting(true)
      const inboxPlanId =
        (drawerProductType === 'inbox' && planId) || currentInbox.plan.planId
      const kbPlanId =
        (drawerProductType === 'kb' && planId) || currentKb.plan.planId
      const data = {
        inboxPlanId,
        kbPlanId,
        billingCycle: newBillingCycle,
      }
      try {
        const response = await updateSubscription(data, {
          successMessage: 'Your upgrade was successful',
          failed: 'Your upgrade has failed',
          convertTrials: false,
        })
        const {
          updateSubscription: {
            pendingSubscription: { approvalUrl } = {},
          } = {},
        } =
          response || {}
        if (approvalUrl) {
          openBillingExternalApprove()
        }
        onExit()
      } catch (error) {
        debug(error)
        setIsSubmitting(false)
      }
    },
    [
      updateSubscription,
      openBillingExternalApprove,
      planId,
      currentKb.plan.planId,
      currentInbox.plan.planId,
      onExit,
      drawerProductType,
      newBillingCycle,
    ]
  )

  const { cardError, closeCardFailureModal } = useBillingModals({})

  const handleConfirmCardFailure = useCallback(
    () => {
      onExit()
      closeCardFailureModal()
    },
    [onExit, closeCardFailureModal]
  )

  useEffect(
    () => {
      dispatch(doFetchBillingData())
    },
    [dispatch]
  )

  useEffect(
    () => {
      if (!newPlan) {
        onExit()
      }
    },
    [newPlan, onExit]
  )

  const footer = (
    <ModalBtns
      css={styles.btnsVariant}
      saveBtnDisabled={isSubmitting || isLoading}
      saveBtnText={isSubmitting ? 'Upgrading...' : 'Upgrade now'}
      onSave={onSubmit}
    />
  )

  const upgradePlanTypeTitle = (
    <>
      You are about to <strong>Upgrade</strong> your{' '}
      <strong>
        {currentBillingCycle} subscription from {currentPlanDisplayName} to{' '}
        {newPlanDisplayName}
      </strong>
    </>
  )

  const upgradeToAnnualTitle = (
    <>
      You are about to change your <strong>Monthly</strong>{' '}
      {currentPlanDisplayName} Plan to <strong>Annual</strong>{' '}
      {newPlanDisplayName} billing.
    </>
  )

  const totalNewCyclePrice = useMemo(
    () => {
      if (!includeOtherProductInBreakdown) return newCyclePrice

      return newCyclePrice + newOtherProductCyclePrice
    },
    [includeOtherProductInBreakdown, newCyclePrice, newOtherProductCyclePrice]
  )

  return (
    <>
      {!showCardError && (
        <AdminAccessDrawer
          title="Upgrade your plan"
          open={open}
          footer={footer}
          onClose={onExit}
          isLoading={isLoading}
        >
          <div className="grui pt-15" css={[styles.container]}>
            <div css={text.styles.textNormal}>
              {isUpgradingToAnnual
                ? upgradeToAnnualTitle
                : upgradePlanTypeTitle}
            </div>
            <div
              css={styles.planChangesCard}
              className="grui pt-10 mt-12 -ml-7 -mr-7"
            >
              {includeOtherProductInBreakdown && (
                <>
                  <div className="grui relative">
                    <div css={[text.styles.text2Xs, styles.tag]}>
                      {PRODUCT_TO_PRODUCT_NAME[drawerProductType]}
                    </div>
                  </div>
                </>
              )}
              <div className="grui pt-2 px-7">
                <PlanRow
                  name={currentPlanDisplayName}
                  product={currentPlan.product}
                  cyclePrice={cyclePrice}
                  itemPrice={itemPrice}
                  users={currentUsageUsers}
                  className="grui mb-8"
                  isUpgradingToAnnual={isUpgradingToAnnual}
                  pricingModel={currentPlan.pricingModel}
                  planCycle={currentBillingCycle}
                  isCycleChange={isCycleChange}
                />
                <PlanRow
                  name={newPlanDisplayName}
                  product={newPlan.product}
                  cyclePrice={newCyclePrice}
                  itemPrice={newItemPrice}
                  users={currentUsageUsers}
                  isNew
                  isUpgradingToAnnual={isUpgradingToAnnual}
                  pricingModel={
                    isUpgradingToAnnual
                      ? currentPlan.pricingModel
                      : newPlan?.pricingModel
                  }
                  planCycle={newBillingCycle}
                  isCycleChange={isCycleChange}
                />
              </div>
              <div
                role="separator"
                css={divider.styles.base}
                className="grui my-9"
              />
              {includeOtherProductInBreakdown && (
                <>
                  <div css={[text.styles.text2Xs, styles.tag]}>
                    {PRODUCT_TO_PRODUCT_NAME[otherProductType]}
                  </div>
                  <div className="grui pt-2 px-7">
                    <PlanRow
                      name={currentOtherProductPlanDisplayName}
                      product={currentOtherProductPlan.product}
                      cyclePrice={currentOtherProductCyclePrice}
                      itemPrice={currentOtherProductItemPrice}
                      users={currentUsageUsers}
                      className="grui mb-8"
                      pricingModel={currentOtherProductPlan.pricingModel}
                      planCycle={currentOtherProductBillingCycle}
                      isCycleChange={isCycleChange}
                      isUpgradingToAnnual={otherProductIsUpgradingToAnnual}
                    />
                    <PlanRow
                      name={newOtherProductPlanDisplayName}
                      product={newOtherProductPlan.product}
                      cyclePrice={newOtherProductCyclePrice}
                      itemPrice={newOtherProductItemPrice}
                      users={currentUsageUsers}
                      pricingModel={
                        otherProductIsUpgradingToAnnual
                          ? currentOtherProductPlan.pricingModel
                          : newOtherProductPlan?.pricingModel
                      }
                      planCycle={newOtherProductBillingCycle}
                      isCycleChange={isCycleChange}
                      isUpgradingToAnnual={otherProductIsUpgradingToAnnual}
                      isNew
                    />
                  </div>
                  <div
                    role="separator"
                    css={divider.styles.base}
                    className="grui my-9"
                  />
                </>
              )}
              <div className="grui px-7">
                {isUpgradingToAnnual &&
                  currentPlan.name === newPlan.name && (
                    <div
                      css={[
                        styles.row,
                        text.styles.textNormal,
                        styles.savingRow,
                      ]}
                      className="grui mt-10 pb-4"
                    >
                      <strong>Your annual savings</strong>
                      <strong>{getFormattedPrice(totalSavings, 'USD')}</strong>
                    </div>
                  )}
                <div css={text.styles.fontMedium} className="grui mt-8 mb-7">
                  <Tooltip
                    title="Your breakdown credit and pro-rata charge are calculated to the minute and may differ slightly when billed."
                    strategy="fixed"
                    portal="#overlays"
                    className="grui py-8"
                  >
                    <span>
                      Breakdown estimate
                      <span
                        css={menuWithSearchStyles.tooltipIcon}
                        className="grui ml-3"
                      >
                        i
                      </span>
                    </span>
                  </Tooltip>
                </div>
                <div css={styles.row}>
                  <div className="grui mb-4 truncate">
                    Credit for remaining days paid for: {refundDays} days
                  </div>
                  <div css={text.styles.fontMedium}>
                    - {getFormattedPrice(remainingPaid, 'USD')}
                  </div>
                </div>
                <div css={styles.row}>
                  <div className="grui truncate">
                    {isUpgradingToAnnual
                      ? `Pro-rata charge for upcoming days: ${chargeDays} days`
                      : `Pro-rata charge for days on ${capitalize(
                          drawerProductType
                        )}: ${newPlan.name} (${chargeDays} days)`}
                  </div>
                  <div css={text.styles.fontMedium}>
                    + {getFormattedPrice(prorataCharges, 'USD')}
                  </div>
                </div>
              </div>

              <div className="grui px-7">
                <div
                  role="separator"
                  css={divider.styles.base}
                  className="grui mt-9"
                />
                <div>
                  <div
                    css={[styles.row, text.styles.fontMedium]}
                    className="grui mt-11"
                  >
                    <div>Total to be charged</div>
                    <div>
                      {getFormattedPrice(prorataCharges - remainingPaid, 'USD')}
                    </div>
                  </div>
                  <div
                    css={[text.styles.text2Xs, styles.note]}
                    className="grui mt-4"
                  >
                    Note: Your credit card ending {primaryCard.last4} will be
                    charged immediately
                  </div>
                </div>
              </div>
              <div css={styles.footer} className="grui py-10 mt-11 px-7">
                <div
                  css={[
                    styles.row,
                    text.styles.fontMedium,
                    text.styles.textWhite,
                  ]}
                >
                  <div>Total for your next recurring invoice</div>
                  <div>{getFormattedPrice(totalNewCyclePrice, currency)}</div>
                </div>

                <div
                  css={[text.styles.text2Xs, text.styles.textWhite]}
                  className="grui opacity-80 mt-2"
                >
                  Bill on {strftime('%A, %Do %B ‘%y', nextBillDate * 1000)}
                </div>
              </div>
            </div>
          </div>
        </AdminAccessDrawer>
      )}
      {showCardError && (
        <CardFailureModal
          onConfirm={handleConfirmCardFailure}
          open={showCardError}
          error={cardError}
        />
      )}
    </>
  )
}

export default BillingUpgradePlan
